// Two panes one over the other with a divider between them (byj-1wv.51.3): a
// share's screen over its timeline. The divider is dragged, or moved with the
// arrow keys as a separator (Home and End to its bounds); how much the top
// pane takes is kept in localStorage under `storageKey`, per browser.
import { useRef, type ReactNode } from "react";
import { dragDivider } from "../drag";
import { useKept } from "../kept";

export const TopMin = 0.25;
export const TopMax = 0.8;
export const TopDefault = 0.55;

export function VSplit({ storageKey, top, bottom, label, topLabel, bottomLabel, only }: {
  storageKey: string;
  top: ReactNode;
  bottom: ReactNode;
  /**
   * One pane alone, the whole height, the other hidden (byj-1wv.51.4): the
   * bottom stays mounted while hidden, so what it holds (a timeline's folds,
   * a comment half written) outlives a tab or the split turned on and off;
   * the top is drawn only while shown, so a player does not play hidden.
   */
  only?: "top" | "bottom";
  /** the divider's name, for a screen reader */
  label: string;
  topLabel: string;
  bottomLabel: string;
}) {
  const [ratio, keep] = useKept(
    storageKey,
    TopDefault,
    (s) => {
      const v = Number(s);
      return Number.isFinite(v) && v >= TopMin && v <= TopMax ? v : undefined;
    },
    (v) => v.toFixed(3),
  );
  const box = useRef<HTMLDivElement>(null);
  const clamp = (r: number) => Math.min(TopMax, Math.max(TopMin, r));
  const set = (r: number) => keep(clamp(r));

  // kept once, as it is let go: not on every move
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = box.current;
    if (!el) return;
    e.preventDefault();
    const { top: y0, height } = el.getBoundingClientRect();
    let at = ratio;
    dragDivider(
      "resizing-rows",
      (ev) => {
        at = clamp((ev.clientY - y0) / height);
        keep(at, false);
      },
      () => keep(at),
    );
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 0.1 : 0.03;
    switch (e.key) {
      case "ArrowUp":
        set(ratio - step);
        break;
      case "ArrowDown":
        set(ratio + step);
        break;
      case "Home":
        set(TopMin);
        break;
      case "End":
        set(TopMax);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  // one tree, split or not, so the bottom pane is never mounted again
  return (
    <div className={`vsplit${only ? ` vsplit-only-${only}` : ""}`} ref={box}>
      <section className="vsplit-top" hidden={only === "bottom"} style={only ? undefined : { flexBasis: `${ratio * 100}%` }} aria-label={topLabel}>
        {only !== "bottom" && top}
      </section>
      <div
        hidden={only !== undefined}
        className="vsplit-divider"
        role="separator"
        aria-orientation="horizontal"
        aria-label={label}
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={TopMin * 100}
        aria-valuemax={TopMax * 100}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        onDoubleClick={() => set(TopDefault)}
      />
      <section className="vsplit-bottom" hidden={only === "top"} aria-label={bottomLabel}>
        {bottom}
      </section>
    </div>
  );
}
